import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { StationV2 } from "@orvis/core";
import { REPORT_REASONS, alreadyReported, submitReport, type ReportReason } from "../reports.js";

/**
 * Small "Report a problem" control: a flag button that opens an inline
 * list of reasons. Lives in the player chrome and in the failed-playback
 * overlay.
 */
export function ReportButton({
  country,
  kind,
  station,
  url,
  compact = false,
}: {
  country: string;
  kind: "tv" | "radio";
  station: StationV2;
  url?: string;
  /** Icon-only trigger (player header) vs. labelled (failed overlay). */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">(() =>
    alreadyReported(country, kind, station) ? "sent" : "idle"
  );

  const send = async (reason: ReportReason) => {
    setState("sending");
    try {
      await submitReport({ country, kind, station, reason, url });
      setState("sent");
      setOpen(false);
    } catch (err) {
      console.warn("[report] failed", err);
      setState("error");
    }
  };

  if (state === "sent") {
    return <span className="text-xs text-text/60 px-2 py-1">{t("report_thanks")}</span>;
  }

  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={t("report_problem")}
        title={t("report_problem")}
        className="px-2 py-1 rounded border border-transparent text-sm text-current/80 hover:border-border focus-visible:border-accent"
      >
        {flagIcon}
        {compact ? null : <span className="ml-1">{t("report_problem")}</span>}
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-border bg-card text-text shadow-lg p-1 text-left"
        >
          {REPORT_REASONS.map((r) => (
            <button
              key={r}
              type="button"
              role="menuitem"
              disabled={state === "sending"}
              onClick={() => void send(r)}
              className="block w-full text-left text-sm px-3 py-2 rounded hover:bg-bg focus-visible:bg-bg disabled:opacity-50"
            >
              {t(`report_${r}`)}
            </button>
          ))}
          {state === "error" ? <div className="text-xs text-red-400 px-3 py-1">{t("report_error")}</div> : null}
        </div>
      ) : null}
    </span>
  );
}

const flagIcon = (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden className="inline-block align-[-3px]">
    <path d="M4 22V4M4 4h12l-2 4 2 4H4" />
  </svg>
);
